import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, ArrowUpRight, Navigation } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DEALERSHIP_INFO, LOGO_PRIMARY, LOGO_SIZES } from '../config/dealership';

// Google Maps URLs
const GOOGLE_MAPS_EMBED_URL = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2466.123456789!2d-8.173!3d51.914!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sOatencake%2C%20Midleton%2C%20Co.%20Cork!5e0!3m2!1sen!2sie!4v1234567890";
const GOOGLE_MAPS_DIRECTIONS_URL = "https://www.google.com/maps/dir/?api=1&destination=Oatencake%2C%20Midleton%2C%20Co.%20Cork%2C%20P25%20KX61%2C%20Ireland";

const Footer = () => {
  const quickLinks = [
    { label: 'Our Stock', href: '/cars' },
    { label: 'Sell Your Car', href: '/sell-your-car' },
    { label: 'Book Service', href: '/repairs' },
    { label: 'Contact Us', href: '/contact' },
  ];

  const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Service', href: '/terms' },
  ];

  return (
    <footer
      className="relative bg-[#050508] border-t border-white/5"
      data-testid="main-footer"
      id="contact"
    >
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
        
        {/* Top Row: Brand, Links, Contact, Hours */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 mb-16">
          
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-5" data-testid="footer-logo-link">
              <img 
                src={LOGO_PRIMARY}
                alt={DEALERSHIP_INFO.name}
                style={{ height: `${LOGO_SIZES.footer}px` }}
                className="w-auto"
                data-testid="footer-logo"
              />
            </Link>
            <p className="text-zinc-500 text-sm leading-relaxed">
              Your trusted partner for premium pre-owned vehicles and expert automotive service in Midleton, Co. Cork.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="label-gold mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="group flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <span className="text-sm">{link.label}</span>
                    <ArrowUpRight 
                      size={12} 
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-[#CCAD60]" 
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="label-gold mb-5">Contact</h4>
            <div className="space-y-4">
              <a 
                href={DEALERSHIP_INFO.phoneHref}
                className="flex items-start gap-3 text-zinc-400 hover:text-white transition-colors group"
                data-testid="footer-contact-phone"
              >
                <Phone size={16} className="text-[#CCAD60] mt-0.5 shrink-0" />
                <span className="text-sm">{DEALERSHIP_INFO.phone}</span>
              </a>
              
              <a 
                href={DEALERSHIP_INFO.emailHref}
                className="flex items-start gap-3 text-zinc-400 hover:text-white transition-colors"
                data-testid="footer-contact-email"
              >
                <Mail size={16} className="text-[#CCAD60] mt-0.5 shrink-0" />
                <span className="text-sm break-all">{DEALERSHIP_INFO.email}</span>
              </a>

              <div 
                className="flex items-start gap-3 text-zinc-400"
                data-testid="footer-contact-location"
              >
                <MapPin size={16} className="text-[#CCAD60] mt-0.5 shrink-0" />
                <span className="text-sm">
                  {DEALERSHIP_INFO.address.line1}<br />
                  {DEALERSHIP_INFO.address.line2}, {DEALERSHIP_INFO.address.city}<br />
                  {DEALERSHIP_INFO.address.postcode}
                </span>
              </div>
            </div>
          </div>

          {/* Opening Hours */}
          <div>
            <h4 className="label-gold mb-5">Opening Hours</h4>
            <div className="flex items-start gap-3" data-testid="footer-contact-hours">
              <Clock size={16} className="text-[#CCAD60] mt-0.5 shrink-0" />
              <div className="space-y-1.5">
                {DEALERSHIP_INFO.hoursFormatted.map(({ day, time, open }) => (
                  <div key={day} className="flex justify-between gap-6 text-sm">
                    <span className="text-zinc-500">{day.slice(0, 3)}</span>
                    <span className={open ? 'text-white' : 'text-zinc-600'}>{time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Find Us Section - Map + Directions */}
        <div className="border-t border-white/5 pt-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            
            {/* Map Column */}
            <div>
              <h4 className="label-gold mb-4">Find Us</h4>
              <div 
                className="relative rounded-lg overflow-hidden border border-[#CCAD60]/20"
                data-testid="footer-map-container"
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d9864.85!2d-8.1755!3d51.9145!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4844908b5b0dd257%3A0x1!2sMidleton%2C%20Co.%20Cork!5e0!3m2!1sen!2sie!4v1706000000000"
                  width="100%"
                  height="200"
                  style={{ border: 0, display: 'block' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="McCarthy Motors Midleton Location"
                  data-testid="footer-map-iframe"
                />
                {/* Subtle gold overlay on corners */}
                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#CCAD60]/30 rounded-tl-lg pointer-events-none" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#CCAD60]/30 rounded-br-lg pointer-events-none" />
              </div>
            </div>

            {/* Directions CTA Column */}
            <div className="text-center lg:text-left">
              <h3 className="font-serif text-2xl text-white mb-3">
                Visit Us
              </h3>
              <p className="text-zinc-400 text-sm mb-6 max-w-md">
                Located in Oatencake, Midleton — just a short drive from Cork City. 
                Come see our vehicles in person and meet our team.
              </p>
              <a
                href={GOOGLE_MAPS_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-gold inline-flex items-center gap-2"
                data-testid="footer-directions-btn"
              >
                <Navigation size={18} />
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Gold Line Separator */}
      <div className="gold-line" />

      {/* Bottom Bar */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-600">
            {DEALERSHIP_INFO.copyright}
          </p>
          <div className="flex items-center gap-6">
            {legalLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="text-xs text-zinc-600 hover:text-[#CCAD60] transition-colors"
                data-testid={`footer-${link.label.toLowerCase().replace(/\s+/g, '-')}-link`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
